/**
 * Defensive timestamp coercion.
 *
 * Firestore stores serverTimestamp() values as Timestamp objects, but our
 * shared-types schemas declare those fields as `z.string().datetime()` for
 * Zod's sake. At runtime hooks read raw Firestore docs, so `createdAt` /
 * `updatedAt` / `dueDate` arrive as Timestamp, never as an ISO string,
 * and `(t ?? "").localeCompare(...)` blows up.
 *
 * This helper accepts any of the shapes we've seen in the wild (ISO
 * string, Date, Firestore Timestamp object, the {seconds, nanoseconds}
 * plain form that some cache paths return) and produces epoch millis.
 *
 * Use for SORTING, not for display — display still wants the formatted
 * string and should branch on type itself.
 */

import { Timestamp } from "firebase/firestore";

export function toMillis(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === "string") {
    const n = Date.parse(v);
    return Number.isNaN(n) ? 0 : n;
  }
  if (v instanceof Date) return v.getTime();
  if (v instanceof Timestamp) return v.toMillis();
  // Duck-typed: Firestore Timestamp from a different SDK build, or a
  // hand-rolled mock in tests.
  const maybe = v as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof maybe.toMillis === "function") return maybe.toMillis();
  if (typeof maybe.seconds === "number") {
    return maybe.seconds * 1000 + (maybe.nanoseconds ?? 0) / 1e6;
  }
  return 0;
}

/** Coerce Firestore Timestamp / ISO string / Date to an ISO-8601 string. */
export function toIsoString(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === "string") {
    const n = Date.parse(v);
    return Number.isNaN(n) ? null : new Date(n).toISOString();
  }
  if (v instanceof Date) return v.toISOString();
  const ms = toMillis(v);
  return ms > 0 ? new Date(ms).toISOString() : null;
}
